import { showAppNotification } from '@/utils/appNotification'
import { resolveApiErrorMessage } from '@/utils/apiResponse'
import { ERROR_CODES, ERROR_I18N_MAP, HTTP_STATUS_TO_ERROR } from '@/constants/errorCodes'
import i18n from '@/i18n'

/**
 * 统一错误处理工具
 */

/**
 * 从 axios 错误中提取错误信息
 * @param {Error} error - axios 错误对象
 * @returns {Object} 错误信息对象
 */
export function extractErrorInfo(error) {
  // 网络错误
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        code: ERROR_CODES.TIMEOUT,
        message: error.message || 'Request timeout'
      }
    }
    return {
      code: ERROR_CODES.NETWORK_ERROR,
      message: error.message || 'Network error'
    }
  }

  // HTTP 错误响应
  const { status, data } = error.response
  const errorCode = HTTP_STATUS_TO_ERROR[status] || ERROR_CODES.UNKNOWN_ERROR
  
  return {
    code: errorCode,
    status,
    message: resolveApiErrorMessage(data, error.message || 'Unknown error'),
    data: data
  }
}

/**
 * 显示错误通知
 * @param {Object} errorInfo - 错误信息对象
 * @param {Object} options - 通知选项
 */
export function showErrorNotification(errorInfo, options = {}) {
  const { t } = i18n.global
  
  // 获取用户友好的错误消息
  const i18nKey = ERROR_I18N_MAP[errorInfo.code]
  const message = i18nKey ? t(i18nKey) : (errorInfo.message || t('Error_Unknown'))
  
  showAppNotification({
    message,
    type: 'error',
    duration: options.duration || 2500,
    position: options.position || 'bottom-right',
    title: options.title,
  })
}

/**
 * 处理 API 错误
 * @param {Error} error - axios 错误对象
 * @param {Object} options - 处理选项
 * @returns {Promise} rejected promise
 */
export function handleApiError(error, options = {}) {
  const errorInfo = extractErrorInfo(error)
  
  // 如果是认证错误，可能需要清除 token 并跳转登录
  if (errorInfo.code === ERROR_CODES.UNAUTHORIZED || errorInfo.code === ERROR_CODES.TOKEN_EXPIRED) {
    // 可以在这里添加清除 token 和跳转登录的逻辑
    // localStorage.removeItem('token')
    // router.push('/login')
  }
  
  // 显示错误通知（除非明确禁用）
  if (options.silent !== true) {
    showErrorNotification(errorInfo, options)
  }
  
  // 返回 rejected promise，包含错误信息
  return Promise.reject({
    ...errorInfo,
    originalError: error
  })
}

/**
 * 错误处理工具对象
 */
const ErrorHandler = {
  extract: extractErrorInfo,
  show: showErrorNotification,
  handle: handleApiError
}

export default ErrorHandler
